import { useEffect, useMemo, useState } from "react";
import { labelsService, type LabelScope, type LabelSummary } from "@/services/labelsService";
import { useScopeOptions } from "@/hooks/useScopeOptions";
import {
  usePageScope,
  useSettingsStore,
  type ScopePageKey,
  type ScopeSelection,
} from "@/store/settingsStore";

/** Entity-wide labels listing — `branchId=0` matches GET /labels?legalEntityId=&branchId=0. */
const ENTITY_WIDE_BRANCH = "0";

/**
 * One GET /labels per legal entity. The list endpoint only filters by a single
 * `legalEntityId` and no longer expands children, so "all entities" and a
 * multi-tick have to be asked for separately.
 */
export function labelListScopes(
  pageScope: ScopeSelection,
  entities: { id: string }[],
  branches: { id: string; legalEntityId: string }[],
  accountLegalEntityId: string | null,
  extra?: { name?: string | null },
): LabelScope[] {
  const name = extra?.name?.trim() || undefined;
  const withMeta = (legalEntityId: string, branchIds?: string[]): LabelScope => ({
    legalEntityId,
    ...(branchIds && branchIds.length > 0 ? { branchIds } : { skipGlobalBranchId: true }),
    skipGlobalContext: true,
    ...(name ? { name } : {}),
  });

  if (pageScope.branchIds.length > 0) {
    const byLegalEntity = new Map<string, string[]>();
    const legalEntityOf = Object.fromEntries(branches.map((b) => [b.id, b.legalEntityId]));
    for (const branchId of pageScope.branchIds) {
      const legalEntityId = legalEntityOf[branchId];
      if (!legalEntityId) continue;
      const ids = byLegalEntity.get(legalEntityId) ?? [];
      ids.push(branchId);
      byLegalEntity.set(legalEntityId, ids);
    }
    return [...byLegalEntity.entries()].map(([legalEntityId, ids]) =>
      withMeta(legalEntityId, [...new Set([...ids.map(String), ENTITY_WIDE_BRANCH])]),
    );
  }

  const entityIds =
    pageScope.legalEntityIds.length > 0
      ? pageScope.legalEntityIds.filter(Boolean)
      : entities.map((e) => e.id).filter(Boolean);
  const ids =
    entityIds.length > 0 ? entityIds : accountLegalEntityId ? [accountLegalEntityId] : [];
  return ids.map((legalEntityId) => withMeta(legalEntityId));
}

export async function fetchLabelsForScope(
  pageScope: ScopeSelection,
  entities: { id: string }[],
  branches: { id: string; legalEntityId: string }[],
  accountLegalEntityId: string | null,
  extra?: { name?: string | null },
): Promise<LabelSummary[]> {
  const scopes = labelListScopes(pageScope, entities, branches, accountLegalEntityId, extra);
  if (scopes.length === 0) return [];
  const batches = await Promise.all(scopes.map((s) => labelsService.listAll(s)));
  return mergeLabelRows(batches);
}

function mergeLabelRows(batches: LabelSummary[][]): LabelSummary[] {
  const seen = new Set<string>();
  const merged: LabelSummary[] = [];
  for (const rows of batches) {
    for (const label of rows) {
      const id = String(label.id ?? "");
      if (!id || seen.has(id)) continue;
      seen.add(id);
      merged.push(label);
    }
  }
  return merged;
}

/**
 * The labels a page's scope picker currently exposes, reloaded whenever that
 * selection — or the account's legal entity — changes.
 *
 * GET /labels takes one legal entity, so each selected entity (or each entity
 * implied by a branch tick) is requested on its own and the rows are merged.
 */
export function useScopeLabels(
  page: ScopePageKey,
  options?: { enabled?: boolean; reloadToken?: number; name?: string },
): { labels: LabelSummary[]; loading: boolean } {
  const enabled = options?.enabled ?? true;
  const reloadToken = options?.reloadToken ?? 0;
  const name = options?.name?.trim() || undefined;
  const scope = usePageScope(page);
  const { entities, branches, loading: scopeLoading } = useScopeOptions();
  const accountLegalEntityId = useSettingsStore((s) => s.legalEntityId);

  const requests = useMemo<LabelScope[] | null>(() => {
    // Profile (entities/branches) is still loading — do not fall through to
    // `branchId=0` on the header entity, or Any label comes back empty on first paint.
    if (scopeLoading) return null;
    const scopes = labelListScopes(scope, entities, branches, accountLegalEntityId, { name });
    return scopes.length > 0 ? scopes : null;
  }, [scope, entities, branches, accountLegalEntityId, scopeLoading, name]);

  const requestKey = JSON.stringify(requests);

  const [labels, setLabels] = useState<LabelSummary[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const scopes = JSON.parse(requestKey) as LabelScope[] | null;
    if (!enabled) {
      setLabels([]);
      setLoading(false);
      return;
    }
    if (!scopes || scopes.length === 0) {
      // Scope options still loading, or there is nothing to query yet.
      setLoading(scopeLoading);
      if (!scopeLoading) setLabels([]);
      return;
    }
    let mounted = true;
    setLoading(true);
    void Promise.all(scopes.map((s) => labelsService.listAll(s)))
      .then((batches) => {
        if (mounted) setLabels(mergeLabelRows(batches));
      })
      .catch(() => {
        if (mounted) setLabels([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [requestKey, enabled, reloadToken, scopeLoading]);

  return { labels, loading };
}

